import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';

import { AiAssessedScore } from '../frontendServices/gameAPI';
import { SocketContext } from '../providers/SocketProvider/SocketProvider';
import {
  hasStartedGeneratingAiFeedback,
  setHasStartedGeneratingAiFeedback,
} from '../utils/liveGameStateStorage';

const useAiAssessedScores = ({
  gameId,
  slideId,
  tenantId,
  setShowMediaIsDisabled,
}: {
  gameId: string;
  slideId: string;
  tenantId: string;
  setShowMediaIsDisabled?: (val: boolean) => void;
}) => {
  const { gameAPI } = useContext(SocketContext);
  const client = useQueryClient();
  const scoresCacheKey = useMemo(() => ['aiAssessedScores', gameId, slideId], [
    gameId,
    slideId,
  ]);
  const {
    mutate: generateAllAiAssessedScores,
    data: allAiAssessedScores,
    isLoading: isFetchingAiAssessedScores,
    error,
  } = useMutation({
    mutationKey: scoresCacheKey,
    mutationFn: async () => {
      if (!gameAPI) return Promise.resolve(undefined);
      // Check if generation has already started for this slide
      if (hasStartedGeneratingAiFeedback(gameId, slideId)) {
        return Promise.resolve(undefined);
      }
      // Mark that generation has started
      setHasStartedGeneratingAiFeedback(gameId, slideId);
      return gameAPI.game.assessAiAnswers({
        gameId,
        slideId,
        tenantId,
      });
    },
    onSuccess: newAiAssessedScore => {
      setShowMediaIsDisabled && setShowMediaIsDisabled(false);
      client.invalidateQueries(['hasReachedLimit']);

      // Invalidate the query so it refetches from the API
      // This is important for image assessments which may take time to process
      client.invalidateQueries(scoresCacheKey);

      client.setQueryData<AiAssessedScore[] | undefined>(
        scoresCacheKey,
        previousScores => {
          // If no new score data, keep what we had
          if (!newAiAssessedScore) return previousScores;
          // Return the new scores, filtering out any null values
          const updatedAiScores =
            newAiAssessedScore.results?.filter(score => score !== null) || [];
          return updatedAiScores;
        },
      );
    },
  });

  const hasStartedGenerating = useMemo(
    () => hasStartedGeneratingAiFeedback(gameId, slideId),
    [gameId, slideId],
  );

  return {
    generateAllAiAssessedScores,
    aiAssessedScores: allAiAssessedScores?.results,
    isFetchingAiAssessedScores,
    hasStartedGenerating,
    error: error,
  };
};

export default useAiAssessedScores;
